(() => {
  if (customElements.get('product-trust')) return;

  const counterPrefix = 'apricot:product-trust:v1:';
  const facesKey = 'apricot:product-trust:faces:v1';
  const quarterHour = 15 * 60 * 1000;
  const memoryStates = new Map();
  let currentFaces = null;
  let lastFaces = [];

  const randomInteger = (min, max) => min + Math.floor(Math.random() * (max - min + 1));
  const integer = (value, fallback) => {
    if (value === null || value === undefined || String(value).trim() === '') return fallback;
    const number = Number(value);
    return Number.isSafeInteger(number) && number >= 0 ? number : fallback;
  };

  function readStorage(name, key) {
    try {
      return JSON.parse(window[name].getItem(key));
    } catch {
      return null;
    }
  }

  function writeStorage(name, key, value) {
    try {
      window[name].setItem(key, JSON.stringify(value));
    } catch {
      // Private browsing and denied storage still allow a working page-local display.
    }
  }

  class ProductTrust extends HTMLElement {
    connectedCallback() {
      this._listeners?.abort();
      this._listeners = new AbortController();
      const options = { signal: this._listeners.signal };
      this._count = this.querySelector('[data-trust-count]');
      this._slider = this.querySelector('[data-trust-slider]');
      this._slides = Array.from(this.querySelectorAll('[data-trust-slide]'));
      this._slideIndex = Math.max(0, this._slides.findIndex(slide => !slide.hidden));
      this._hovering = false;
      this._pageSuspended = false;
      this._motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this._autoplay = this.dataset.autoplay !== 'false';
      this._interval = Math.max(2, Math.min(60, Number(this.dataset.interval) || 5)) * 1000;
      this._storageKey = counterPrefix + this.dataset.productId;
      const min = integer(this.dataset.countMin, null);
      const max = integer(this.dataset.countMax, null);
      const hasRange = min !== null && max !== null;
      this._min = hasRange ? Math.min(min, max) : min;
      this._max = hasRange ? Math.max(min, max) : max;
      this._override = integer(this.dataset.countOverride, null);
      this._counterEnabled = !!this._count && !!this.dataset.productId && (hasRange || this._override !== null);
      this._config = JSON.stringify([this._min, this._max, this._override]);
      const purchase = this.querySelector('[data-trust-purchase]');
      if (purchase) {
        purchase.hidden = !this._counterEnabled;
        const block = this.closest('[data-trust-block]');
        if (block) block.hidden = !this._counterEnabled;
      }
      try {
        this._formatter = new Intl.NumberFormat(document.documentElement.lang || undefined);
      } catch {
        this._formatter = new Intl.NumberFormat();
      }

      const remembered = memoryStates.has(this._storageKey);
      this._newVisit = !remembered;
      if (this._counterEnabled) this._selectFaces();
      if (this._counterEnabled && this._count && this.dataset.productId) {
        this._state = this._readState();
        this._renderCount();
        if (!document.hidden) this._activateCounter();
      }
      this._setupSlider(options);

      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this._deactivateCounter();
        else this._activateCounter();
        this._scheduleSlider();
      }, options);
      window.addEventListener('pagehide', () => {
        this._pageSuspended = true;
        this._deactivateCounter();
        this._scheduleSlider();
      }, options);
      window.addEventListener('pageshow', event => {
        this._pageSuspended = false;
        if (event.persisted && this._counterEnabled) this._selectFaces(true);
        if (!document.hidden) this._activateCounter();
        this._scheduleSlider();
      }, options);
      window.addEventListener('storage', event => {
        if (event.key !== this._storageKey || !this._count || !this._counterEnabled) return;
        this._state = this._readState();
        this._renderCount();
        if (this._counterActive) this._scheduleCounter();
      }, options);
    }

    disconnectedCallback() {
      this._deactivateCounter();
      clearTimeout(this._sliderTimer);
      this._listeners?.abort();
    }

    _validState(state) {
      return state?.version === 1 && state.config === this._config &&
        Number.isSafeInteger(state.count) && state.count >= 0 &&
        Number.isFinite(state.lastSeenAt) && state.lastSeenAt >= 0 &&
        (state.awaySince === null || (Number.isFinite(state.awaySince) && state.awaySince >= 0)) &&
        Number.isFinite(state.awayRemainder) && state.awayRemainder >= 0 && state.awayRemainder < quarterHour &&
        Number.isFinite(state.nextIncrementAt) && state.nextIncrementAt >= 0;
    }

    _readState() {
      const saved = readStorage('localStorage', this._storageKey);
      const memory = memoryStates.get(this._storageKey);
      const newerMemory = this._validState(memory) && (!this._validState(saved) ||
        memory.lastSeenAt > saved.lastSeenAt ||
        (memory.lastSeenAt === saved.lastSeenAt && memory.count > saved.count));
      const state = newerMemory ? { ...memory } : this._validState(saved) ? saved : {
        version: 1,
        config: this._config,
        count: this._override ?? randomInteger(this._min, this._max),
        lastSeenAt: Date.now(),
        awaySince: null,
        awayRemainder: 0,
        nextIncrementAt: 0
      };
      memoryStates.set(this._storageKey, { ...state });
      return state;
    }

    _saveState() {
      memoryStates.set(this._storageKey, { ...this._state });
      writeStorage('localStorage', this._storageKey, this._state);
    }

    _renderCount() {
      if (this._count && this._state) this._count.textContent = this._formatter.format(this._state.count);
    }

    _activateCounter(newVisit = this._newVisit) {
      if (!this._counterEnabled || !this._count || !this.dataset.productId || this._counterActive || document.hidden || this._pageSuspended) return;
      this._state = this._readState();
      const now = Date.now();
      const awaySince = this._state.awaySince ?? (newVisit ? this._state.lastSeenAt : null);
      if (awaySince !== null) {
        const away = Math.max(0, now - awaySince) + this._state.awayRemainder;
        const increase = Math.floor(away / quarterHour);
        this._state.count = Math.min(Number.MAX_SAFE_INTEGER, this._state.count + increase);
        this._state.awayRemainder = away % quarterHour;
        this._state.nextIncrementAt = 0;
      }
      this._state.awaySince = null;
      this._newVisit = false;
      this._state.lastSeenAt = now;
      if (this._state.nextIncrementAt <= now) this._state.nextIncrementAt = now + randomInteger(60000, 120000);
      this._counterActive = true;
      this._saveState();
      this._renderCount();
      this._scheduleCounter();
    }

    _deactivateCounter() {
      clearTimeout(this._counterTimer);
      if (!this._counterActive || !this._count) return;
      this._counterActive = false;
      this._state = this._readState();
      const now = Date.now();
      this._state.awaySince ??= now;
      this._state.lastSeenAt = now;
      this._state.nextIncrementAt = 0;
      this._saveState();
    }

    _scheduleCounter() {
      clearTimeout(this._counterTimer);
      if (!this._counterActive || !this.isConnected || document.hidden || this._pageSuspended) return;
      const delay = Math.max(1, (this._state.nextIncrementAt || Date.now() + 60000) - Date.now());
      this._counterTimer = setTimeout(() => this._incrementCounter(), delay);
    }

    _incrementCounter() {
      if (!this._counterActive || !this.isConnected || document.hidden || this._pageSuspended) return;
      // Reconcile first so a second tab does not overwrite a newer displayed count.
      this._state = this._readState();
      const now = Date.now();
      if (this._state.nextIncrementAt <= now) {
        this._state.count = Math.min(Number.MAX_SAFE_INTEGER, this._state.count + randomInteger(1, 3));
        this._state.nextIncrementAt = now + randomInteger(60000, 120000);
      }
      this._state.awaySince = null;
      this._state.lastSeenAt = now;
      this._saveState();
      this._renderCount();
      this._scheduleCounter();
    }

    _selectFaces(force = false) {
      const container = this.querySelector('[data-trust-faces]');
      const template = this.querySelector('template[data-trust-image-pool]');
      if (!container || !template) return;
      const pool = [];
      const identities = new Set();
      const sources = new Set();
      template.content.querySelectorAll('img[data-src]').forEach(image => {
        const src = image.dataset.src;
        const id = image.dataset.imageId || src;
        if (!src || identities.has(id) || sources.has(src)) return;
        identities.add(id);
        sources.add(src);
        pool.push({ id, image });
      });
      const signature = JSON.stringify(pool.map(item => [item.id, item.image.dataset.src]));
      if (force || currentFaces?.productId !== this.dataset.productId || currentFaces?.signature !== signature) {
        const stored = readStorage('sessionStorage', facesKey);
        const previous = Array.isArray(stored) ? stored.filter(id => typeof id === 'string') : lastFaces;
        let candidates = pool;
        if (pool.length >= 4) candidates = pool.filter(item => !previous.includes(item.id));
        let selected = [];
        if (pool.length === 3) {
          const pairs = [[pool[0], pool[1]], [pool[0], pool[2]], [pool[1], pool[2]]]
            .filter(pair => !pair.every(item => previous.includes(item.id)));
          selected = pairs[randomInteger(0, pairs.length - 1)];
        } else {
          candidates = [...candidates];
          while (selected.length < 2 && candidates.length) {
            selected.push(candidates.splice(randomInteger(0, candidates.length - 1), 1)[0]);
          }
        }
        lastFaces = selected.map(item => item.id);
        currentFaces = { productId: this.dataset.productId, signature, ids: lastFaces };
        writeStorage('sessionStorage', facesKey, lastFaces);
      }
      const images = currentFaces.ids.map(id => pool.find(item => item.id === id)).filter(Boolean).map(({ image }) => {
        const selected = image.cloneNode(true);
        selected.src = selected.dataset.src;
        selected.removeAttribute('data-src');
        selected.decoding = 'async';
        return selected;
      });
      container.replaceChildren(...images);
      container.hidden = images.length === 0;
    }

    _setupSlider(options) {
      if (!this._slider || !this._slides.length) return;
      this._slider.addEventListener('pointerenter', event => {
        if (event.pointerType === 'touch') return;
        this._hovering = true;
        this._scheduleSlider();
      }, options);
      this._slider.addEventListener('pointerleave', () => {
        this._hovering = false;
        this._scheduleSlider();
      }, options);
      this._slider.addEventListener('focusin', () => this._scheduleSlider(), options);
      this._slider.addEventListener('focusout', () => queueMicrotask(() => this._scheduleSlider()), options);
      this._slider.addEventListener('keydown', event => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        const direction = document.documentElement.dir === 'rtl' ? -1 : 1;
        this._showSlide(this._slideIndex + (event.key === 'ArrowRight' ? direction : -direction), true);
      }, options);
      this._slider.addEventListener('pointerdown', event => {
        if (!event.isPrimary || event.target.closest('button, a')) return;
        this._pointerStart = { x: event.clientX, y: event.clientY };
      }, options);
      this._slider.addEventListener('pointerup', event => {
        const start = this._pointerStart;
        this._pointerStart = null;
        if (!start) return;
        const dx = event.clientX - start.x;
        const dy = event.clientY - start.y;
        if (Math.abs(dx) < 35 || Math.abs(dx) <= Math.abs(dy) * 1.2) return;
        const direction = document.documentElement.dir === 'rtl' ? -1 : 1;
        this._showSlide(this._slideIndex + (dx < 0 ? direction : -direction), true);
      }, options);
      this._slider.addEventListener('pointercancel', () => { this._pointerStart = null; }, options);
      this._motion.addEventListener('change', () => this._scheduleSlider(), options);
      this._showSlide(this._slideIndex);
    }

    _showSlide(index, manual = false) {
      if (!this._slides.length) return;
      this._slideIndex = (index + this._slides.length) % this._slides.length;
      this._slides.forEach((slide, slideIndex) => {
        const selected = slideIndex === this._slideIndex;
        slide.hidden = !selected;
        slide.classList.toggle('is-selected', selected);
        slide.setAttribute('aria-hidden', String(!selected));
        slide.setAttribute('aria-label', `${slideIndex + 1} / ${this._slides.length}`);
      });
      const status = this.querySelector('[data-trust-status]');
      if (manual && status) status.textContent = this._slides[this._slideIndex].textContent.trim();
      this._scheduleSlider();
    }

    _scheduleSlider() {
      clearTimeout(this._sliderTimer);
      if (!this._slider) return;
      const available = this._autoplay && this._slides.length > 1 && !this._motion.matches;
      if (!available || this._hovering || document.hidden || this._pageSuspended ||
        !this.isConnected || this._slider.contains(document.activeElement)) return;
      this._sliderTimer = setTimeout(() => this._showSlide(this._slideIndex + 1), this._interval);
    }
  }

  customElements.define('product-trust', ProductTrust);
})();
